"use client";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
	faCalendarAlt,
	faChild,
	faCoins,
	faDragon,
	faEnvelope,
	faGift,
	faMapMarkerAlt,
	faStar,
	faTicketAlt,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

const EventsPage = () => {
	const [showCopyNotification, setShowCopyNotification] = useState(false);

	const eventTypes = [
		{
			id: "kids",
			title: "Eventos Infantiles",
			icon: faChild,
			description: {
				paragraphs: [
					<>
						La serie de televisión <em>«Stranger Things»</em> y la nueva película{" "}
						<em>«Honor entre ladrones»</em> han sido la toma de contacto de muchos niños y niñas con{" "}
						<strong>Dragones & Mazmorras</strong>, lo que ha hecho crecer rápidamente su popularidad entre los
						más jóvenes.
					</>,
					<>
						Los juegos de rol son una gran manera para que exploren su imaginación, así como para aprender a
						resolver problemas. Cada vez que hemos introducido a un grupo de niños y niñas se lo han pasado
						fantásticamente bien, con comentarios como <em>«LO MEJOR DEL MUNDO»</em>, algo con lo que estamos
						completamente de acuerdo.
					</>,
				],
			},
			benefits: [
				"Aprenda habilidades que le ayuden a resolver problemas",
				"Explore su imaginación",
				"Aprenda a aplicar de forma práctica las habilidades básicas de matemáticas y escritura",
				"Aprenda el valor del trabajo en equipo para lograr un objetivo común",
				"Comprenda la diversidad y cómo respetar a los demás a pesar de sus diferencias",
				"Gane confianza",
				"Se sienta como un héroe",
				"Se divierta como no veas",
			],
			image: "/images/eventos-ninos.webp",
			color: "border-purple-500",
			accentColor: "bg-purple-600",
			textColor: "text-purple-400",
		},
		{
			id: "teambuilding",
			title: "Team Building",
			icon: faUsers,
			description: {
				paragraphs: [
					<>
						Si lo que quieres es unir a los miembros de tu equipo, abandonarlos en una isla desierta en medio
						del pacífico es una de las mejores maneras de hacerlo.
					</>,
					<>
						Si además no quieres cometer varios crímenes en el proceso, ¿por qué no embarcarlos en una partida
						de <strong>Cocos & Corazones</strong>? Una partida de 2 a 4 horas desde 15€ por jugador donde
						tendrán que sobrevivir peligros desconocidos. Y si las aventuras tropicales no son lo tuyo, prueba
						alguna de las otras ambientaciones que ofrecemos.
					</>,
					<>
						Usando un sencillo sistema adaptado de <strong>Láseres & Sentimientos</strong>, tienes todas las
						facilidades para que la próxima reunión a la que vayas sea la más divertida de tu vida.
					</>,
				],
			},
			features: [
				"Fomenta el trabajo en equipo",
				"Desafía la resolución creativa de problemas",
				"Crea vínculos duraderos entre los miembros del equipo",
				"Adaptable a cualquier grupo y tamaño de equipo",
				"Disponible en castellano e inglés",
			],
			image: "/images/eventos-teambuilding.webp",
			color: "border-blue-500",
			accentColor: "bg-blue-600",
			textColor: "text-blue-400",
		},
		{
			id: "birthdays",
			title: "Cumpleaños Mágicos",
			icon: faGift,
			description: {
				paragraphs: [
					<>
						¿Cansado de las mismas celebraciones de siempre? Convierte el próximo cumpleaños en una aventura
						épica donde los protagonistas sean los invitados. El festejado se convertirá en el héroe de su
						propia historia, acompañado por sus amigos en una misión diseñada específicamente para la ocasión.
					</>,
					<>
						Para todas las edades, nuestras aventuras de cumpleaños son memorables y totalmente
						personalizables. Desde batallas contra dragones hasta misiones espaciales, el límite es vuestra
						imaginación.
					</>,
				],
			},
			features: [
				"Aventuras personalizadas según la edad y gustos",
				"Atrezzo y ambientación temática disponible",
				"Duración adaptable de 2 a 6 horas",
				"Posibilidad de integrar la temática de la fiesta",
				"Recuerdos únicos para todos los participantes",
				"¡Un regalo precioso que te acompañará en tus sesiones roleras!",
			],
			image: "/images/eventos-cumpleanos.webp",
			color: "border-red-500",
			accentColor: "bg-red-600",
			textColor: "text-red-400",
		},
		{
			id: "special",
			title: "Eventos Especiales",
			icon: faCalendarAlt,
			description: {
				paragraphs: [
					<>
						Para esas ocasiones que merecen algo extraordinario, ofrecemos experiencias de rol a medida.
						Comuniones, despedidas de soltero/a, reuniones familiares, fiestas temáticas... cualquier evento
						puede transformarse en el escenario perfecto para una aventura inolvidable.
					</>,
					<>
						Nuestros Amos del Calabozo se adaptan a la naturaleza de tu evento, creando historias que reflejen
						su espíritu y proporcionando una experiencia única que vuestros invitados comentarán durante años.
					</>,
				],
			},
			features: [
				"Totalmente adaptable a la naturaleza de tu evento",
				"Posibilidad de incluir elementos personales en la narrativa",
				"Adecuado para grupos diversos y edades mixtas",
				"Versiones cortas o extendidas según necesidades",
				"Experiencia multilingüe disponible",
			],
			image: "/images/eventos-especiales.webp",
			color: "border-yellow-500",
			accentColor: "bg-yellow-600",
			textColor: "text-yellow-400",
		},
	];

	return (
		<main className="bg-black text-white">
			{/* Hero Section */}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/eventos_header.webp')" }}
			>
				<div className="absolute inset-0 bg-black/70"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">Eventos Mágicos</span>
					</motion.h1>

					<motion.p
						className="text-2xl text-gray-300 max-w-3xl mx-auto text-center mb-8"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="text-red-400">
							Cualquier reunión es mejor con un Amo del Calabozo
							<br />
							<span className="text-xl italic text-gray-300">
								Adecuado para todas las edades, añade emoción a tu ocasión especial embarcándote en una
								trepidante aventura con tus colegas, compartiendo una historia que recordaréis en los años
								venideros.
							</span>
						</strong>
					</motion.p>

					<motion.div
						className="text-center"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.6, duration: 0.6 }}
					>
						<p className="text-xl text-gray-200 max-w-2xl mx-auto mb-6">
							La mejor manera de amenizar cualquier cumpleaños, evento de team building, comunión, o cualquier
							otra reunión social o profesional, es una partida de rol con{" "}
							<span className="font-amarante text-red-400">Los Amos del Calabozo</span>.
						</p>
						<p className="text-lg text-white bg-red-800/50 rounded-lg py-2 px-4 inline-block">
							Todas nuestras partidas están disponibles tanto en castellano como{" "}
							<a href="/aprende-ingles" className="text-red-400 underline">
								en inglés.
							</a>
						</p>
					</motion.div>
				</div>
			</section>

			{/* Event Types Section */}
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faDragon} className="mr-3 text-red-500" />
						Tipos de Eventos
					</motion.h2>

					<div className="flex flex-col gap-16">
						{eventTypes.map((event, index) => (
							<motion.div
								key={event.id}
								className={`bg-gray-800 rounded-lg overflow-hidden border-l-4 ${event.color}`}
								initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true }}
								transition={{ delay: index * 0.2, duration: 0.6 }}
							>
								<div className="p-8">
									<div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
										<div
											className={`w-20 h-20 ${event.accentColor} rounded-full flex items-center justify-center flex-shrink-0 shadow-lg`}
										>
											<FontAwesomeIcon icon={event.icon} className="text-white text-3xl" />
										</div>
										<div>
											<h3 className={`text-3xl font-amarante headerFont ${event.textColor}`}>
												{event.title}
											</h3>
										</div>
									</div>

									<div className="space-y-6">
										{event.description.paragraphs.map((paragraph, idx) => (
											<p key={idx} className="text-gray-300">
												{paragraph}
											</p>
										))}

										{event.benefits && (
											<div className="mt-6">
												<h4 className="text-xl font-amarante text-red-300 mb-4">
													<FontAwesomeIcon icon={faStar} className="mr-2" />
													En una partida de rol puedes esperar que tu hijo o hija:
												</h4>
												<ul className="list-none grid grid-cols-1 md:grid-cols-2 gap-3">
													{event.benefits.map((benefit, idx) => (
														<li key={idx} className="text-gray-300 flex items-start">
															<span className="text-red-400 mr-2">•</span> {benefit}
														</li>
													))}
												</ul>
											</div>
										)}

										{event.features && (
											<div className="mt-6">
												<h4 className="text-xl font-amarante text-red-300 mb-4">
													<FontAwesomeIcon icon={faStar} className="mr-2" />
													Características del evento:
												</h4>
												<ul className="list-none grid grid-cols-1 md:grid-cols-2 gap-3">
													{event.features.map((feature, idx) => (
														<li key={idx} className="text-gray-300 flex items-start">
															<span className="text-red-400 mr-2">•</span> {feature}
														</li>
													))}
												</ul>
											</div>
										)}

										<div
											className={`${event.accentColor}/20 p-6 rounded-lg mt-6 border border-${event.color.split("-")[1]}-800`}
										>
											<div className="flex items-center gap-3 mb-3">
												<FontAwesomeIcon icon={faMapMarkerAlt} className={event.textColor} />
												<h4 className={`text-lg font-amarante ${event.textColor}`}>¿Dónde y cómo?</h4>
											</div>
											<p className="text-gray-300">
												Nos desplazamos a tu ubicación dentro de la Comunidad de Madrid o podemos facilitar el
												espacio para tu evento. Contacta con nosotros para discutir los detalles y crear una
												experiencia totalmente personalizada.
											</p>
										</div>

										<div
											className={`${event.accentColor}/20 p-6 rounded-lg mt-2 border border-${event.color.split("-")[1]}-800`}
										>
											<div className="flex items-center gap-3 mb-3">
												<FontAwesomeIcon icon={faCoins} className={event.textColor} />
												<h4 className={`text-lg font-amarante ${event.textColor}`}>Tarifas</h4>
											</div>
											<p className="text-gray-300">
												Precios desde 15€ por participante, dependiendo del tipo de evento, duración y
												necesidades específicas. Solicita un presupuesto personalizado sin compromiso.
											</p>
										</div>
									</div>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* Testimonials Section */}
			{/* <section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faHeart} className="mr-3 text-red-500" />
						Testimonios Arcanos
					</motion.h2>
					
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
						{[
							{
								quote: "¡Mi hijo y sus amigos estuvieron hablando de la aventura durante semanas! Nunca los había visto tan entusiasmados con algo.",
								author: "María L., Madre de aventurero de 10 años",
								type: "Cumpleaños infantil"
							},
							{
								quote: "Contratamos una sesión para el team building de nuestra empresa y fue revelador. Descubrimos aspectos de nuestros compañeros que nunca hubiéramos imaginado.",
								author: "Carlos G., Director de RRHH",
								type: "Team Building empresarial"
							},
							{
								quote: "La comunión de mi hija se convirtió en una experiencia mágica. Incluso los abuelos acabaron participando y disfrutando como niños.",
								author: "Laura S., Evento familiar",
								type: "Comunión"
							},
							{
								quote: "Gracias por convertir nuestra despedida de soltero en una aventura épica y no en la típica noche de excesos que olvidaríamos al día siguiente.",
								author: "Grupo de amigos del novio",
								type: "Despedida de soltero"
							},
						].map((testimonial, index) => (
							<motion.div
								key={index}
								className="bg-gray-800 rounded-lg p-6 border-l-4 border-red-700"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{ delay: index * 0.1, duration: 0.6 }}
							>
								<p className="text-gray-300 italic mb-4">"{testimonial.quote}"</p>
								<div className="flex justify-between items-center">
									<p className="text-red-400 font-bold">{testimonial.author}</p>
									<span className="bg-red-900/30 text-red-300 px-3 py-1 rounded-full text-sm">{testimonial.type}</span>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</section> */}

			{/* Final CTA */}
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="bg-gray-800 rounded-lg p-8 text-center border-4 border-red-700"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<motion.div
							whileHover={{ scale: 1.05 }}
							className="mx-auto w-24 h-24 bg-red-600 rounded-full flex items-center justify-center mb-6"
						>
							<FontAwesomeIcon icon={faTicketAlt} className="text-white text-4xl" />
						</motion.div>
						<h2 className="text-3xl font-amarante headerFont text-red-400 mb-4">La aventura os espera...</h2>
						<p className="text-gray-300 text-xl mb-8">
							Desde cumpleaños mágicos hasta intensos retos de team building, estamos listos para transformar
							cualquier evento en una experiencia extraordinaria. ¿Qué aventura elegirás para tu próxima
							celebración?
						</p>
						<motion.div
							className="flex flex-col md:flex-row items-center justify-center gap-4"
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
						>
							<motion.a
								href="mailto:somos@losamosdelcalabozo.com?subject=Consulta sobre Eventos Especiales"
								className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
								whileHover={{
									scale: 1.1,
									boxShadow: "0 0 15px #f93d3d",
									backgroundColor: "white",
									color: "black",
									fontWeight: "900",
									fontStyle: "italic",
								}}
								whileTap={{ scale: 0.98 }}
							>
								<FontAwesomeIcon icon={faEnvelope} className="mr-2" />
								Reservar mi Evento
							</motion.a>

							<motion.a
								href={`https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, me gustaría obtener más información sobre vuestros eventos especiales. ¿Podéis ayudarme?")}`}
								className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
								whileHover={{
									scale: 1.05,
									boxShadow: "0 0 15px #22c55e",
								}}
								whileTap={{ scale: 0.98 }}
								target="_blank"
								rel="noopener noreferrer"
							>
								<FontAwesomeIcon icon={faWhatsapp} className="mr-2" />
								Consultar por WhatsApp
							</motion.a>
						</motion.div>
					</motion.div>
				</div>
			</section>
			{/* Main Contact Methods */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					{/* Email Address Display */}
					<motion.div
						className="mt-8 sm:mt-10 md:mt-12 bg-gray-800 rounded-lg p-4 sm:p-6 md:p-8 text-center relative"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.8, duration: 0.6 }}
					>
						<h3 className="text-xl sm:text-2xl font-amarante headerFont text-red-400 mb-4">
							Nuestro Pergamino Oficial
						</h3>
						<motion.div
							className="text-lg sm:text-2xl md:text-4xl font-bold text-white bg-gray-700 py-3 px-2 sm:px-4 rounded-lg select-all cursor-pointer"
							whileHover={{
								backgroundColor: "#3b82f6",
								color: "white",
								transition: { duration: 0.3 },
							}}
							onClick={() => {
								navigator.clipboard.writeText("somos@losamosdelcalabozo.com");
								setShowCopyNotification(true);
								setTimeout(() => setShowCopyNotification(false), 2000);
							}}
						>
							somos@losamosdelcalabozo.com
						</motion.div>
						<p className="text-gray-400 mt-2 text-xs sm:text-sm italic">
							Haz click para copiar a tu grimorio de hechizos (portapapeles)
						</p>
						<AnimatePresence>
							{showCopyNotification && (
								<motion.div
									className="bg-green-600 text-white text-sm py-2 px-4 rounded-md absolute left-1/2 transform -translate-x-1/2 mt-2"
									initial={{ opacity: 0, y: -10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0 }}
								>
									Email copiado al portapapeles
								</motion.div>
							)}
						</AnimatePresence>
					</motion.div>
				</div>
			</section>

			{/* Easter Egg */}
			<section className="py-4 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="text-center opacity-30 hover:opacity-100 transition-opacity cursor-pointer"
						whileHover={{ scale: 1.01 }}
						whileTap={{ scale: 0.99 }}
					>
						<p className="text-sm text-gray-500 italic">
							Cuenta la leyenda que los eventos bendecidos por un Amo del Calabozo traen buena fortuna y
							recuerdos inolvidables a todos los participantes...
						</p>
					</motion.div>
				</div>
			</section>
		</main>
	);
};

export default EventsPage;
